import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building } from "lucide-react";
import { useEffect, useState } from "react";
import { persistFirmInviteToken } from "@/components/onboarding/accept-firm-invite";
import { getEnvBadgeConfig } from "@/lib/env-badge";

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

const PLATFORM_NAME = "ClaimHive";
const PLATFORM_TAGLINE = "Public adjusting operations for independent firms";

export default function Landing() {
  const [isLoading, setIsLoading] = useState(false);
  const [betaCode, setBetaCode] = useState('');
  const [inviteToken, setInviteToken] = useState('');

  useEffect(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('invite');
    if (fromUrl) {
      persistFirmInviteToken(fromUrl);
      setInviteToken(fromUrl);
      return;
    }
    try {
      setInviteToken(localStorage.getItem('ch_firm_invite') || '');
    } catch {
      setInviteToken('');
    }
  }, []);

  const handleGoogleLogin = () => {
    if (betaCode.trim()) {
      document.cookie = `ch_beta_code=${encodeURIComponent(betaCode.trim())}; path=/; max-age=3600; SameSite=Lax`;
      localStorage.setItem('ch_beta_code', betaCode.trim());
    }
    if (inviteToken) {
      persistFirmInviteToken(inviteToken);
    }
    setIsLoading(true);

    // Open Google OAuth in a popup window
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const loginParams = new URLSearchParams();
    if (betaCode.trim()) loginParams.set('betaCode', betaCode.trim());
    if (inviteToken) loginParams.set('invite', inviteToken);
    const loginUrl = loginParams.toString() ? `/api/login?${loginParams}` : '/api/login';
    const popup = window.open(
      loginUrl,
      'google-login',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=yes,status=no`
    );

    // Poll for popup close
    const pollInterval = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(pollInterval);
        setIsLoading(false);
        // Navigate to root — auth state will re-evaluate and redirect to dashboard
        window.location.href = '/';
      }
    }, 500);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 rounded-lg bg-primary flex items-center justify-center">
              <Building className="h-8 w-8 text-primary-foreground" />
            </div>
          </div>
          <CardTitle className="text-2xl">{PLATFORM_NAME}</CardTitle>
          <p className="text-muted-foreground">{PLATFORM_TAGLINE}</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-center text-sm text-muted-foreground">
              Sign in with Google. Beta firms need an invite or a beta code.
            </p>
            <div className="space-y-2">
              <Label htmlFor="beta-code">Beta code (optional)</Label>
              <Input
                id="beta-code"
                value={betaCode}
                onChange={(e) => setBetaCode(e.target.value)}
                placeholder="Enter a superadmin beta code"
                data-testid="input-landing-beta-code"
              />
            </div>
            <Button 
              className="w-full" 
              variant="outline"
              onClick={handleGoogleLogin}
              disabled={isLoading}
              data-testid="button-login"
            >
              {isLoading ? (
                <span className="animate-spin mr-2 h-5 w-5 border-2 border-current border-t-transparent rounded-full inline-block" />
              ) : (
                <GoogleIcon className="h-5 w-5 mr-2" />
              )}
              {isLoading ? 'Signing in...' : 'Sign in with Google'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Environment & Version */}
      {(() => {
        const env = (import.meta.env.VITE_APP_ENV || 'local').toLowerCase();
        const version = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';
        const config = getEnvBadgeConfig(env);
        return (
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <span className={`h-2 w-2 rounded-full ${config.dot}`} />
            <span className={`font-semibold ${config.color}`}>{config.label}</span>
            <span>·</span>
            <span className="font-mono">v{version}</span>
          </div>
        );
      })()}
    </div>
  );
}

